import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FormField } from '../components/ui';
import Seo from '../components/Seo';

const btnStyle = {
  primary: {
    display:'block', width:'100%', padding:'13px 24px', borderRadius:8,
    fontWeight:600, fontSize:15, cursor:'pointer', border:'none',
    background:'#1a1a18', color:'#fff', fontFamily:'inherit', transition:'all .15s',
  },
  ghost: {
    display:'block', width:'100%', padding:'10px 24px', borderRadius:8,
    fontWeight:500, fontSize:13, cursor:'pointer',
    background:'transparent', color:'var(--ink-3)', border:'none',
    fontFamily:'inherit',
  },
};

function AuthShell({ children, title, sub }) {
  const { pathname } = useLocation();
  return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
      <Seo
        title={pathname === '/signup' ? 'Start your free trial | LandlordComply' : 'Sign in | LandlordComply'}
        path={pathname}
        noindex
      />
      <div style={{ width:'100%', maxWidth:420 }}>
        <div style={{ textAlign:'center', marginBottom:40 }}>
          <h1 style={{ fontSize:36, fontWeight:300 }}>Landlord<em>Comply</em></h1>
          <p style={{ fontSize:13, color:'var(--ink-3)', marginTop:6 }}>UK Compliance Hub</p>
        </div>
        <div style={{ background:'var(--bg-card)', border:'1.5px solid var(--border)', borderRadius:14, padding:36, boxShadow:'var(--shadow)' }}>
          <h2 style={{ fontSize:22, marginBottom:6 }}>{title}</h2>
          <p style={{ fontSize:14, color:'var(--ink-3)', marginBottom:28 }}>{sub}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

export function LoginPage() {
  const { login }      = useAuth();
  const navigate       = useNavigate();
  const [searchParams] = useSearchParams();

  const [step, setStep]       = useState('email');
  const [email, setEmail]     = useState('');
  const [code, setCode]       = useState('');
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const magicEmail = searchParams.get('email');
    const magicCode  = searchParams.get('code');
    if (magicEmail && magicCode) {
      setEmail(magicEmail);
      setCode(magicCode);
      setStep('code');
      verifyOTP(magicEmail, magicCode);
    }
  }, []);

  async function handleRequestOTP(e) {
    e?.preventDefault();
    setLoading(true); setError('');
    try {
      const accessCode = localStorage.getItem('lc_access_code');
      const res = await fetch('/api/auth/request-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(accessCode ? { 'X-Access-Code': accessCode } : {}) },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send code');
      setStep('code');
    } catch (err) {
      setError(err.message);
    } finally { setLoading(false); }
  }

  async function verifyOTP(emailVal, codeVal) {
    setLoading(true); setError('');
    try {
      const accessCode = localStorage.getItem('lc_access_code');
      const headers = { 'Content-Type': 'application/json' };
      if (accessCode) headers['X-Access-Code'] = accessCode;

      const res = await fetch('/api/auth/verify-otp', {
        method: 'POST', headers,
        body: JSON.stringify({ email: emailVal, code: codeVal }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Invalid code');

      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.landlord));

      const billingRes = await fetch('/api/billing/status', {
        headers: { Authorization: `Bearer ${data.token}`, ...(accessCode ? { 'X-Access-Code': accessCode } : {}) }
      });
      const billingData = await billingRes.json();
      const needsBilling = billingData.sub_status !== 'active' &&
                           billingData.sub_status !== 'trialing' &&
                           new Date(data.landlord.created_at) >= new Date('2026-04-13');

      if (needsBilling) {
        const checkoutRes = await fetch('/api/billing/create-trial-checkout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${data.token}`, ...(accessCode ? { 'X-Access-Code': accessCode } : {}) },
          body: JSON.stringify({ plan: billingData.plan || 'starter' }),
        });
        const checkoutData = await checkoutRes.json();
        if (checkoutData.url) {
          window.location.href = checkoutData.url;
          return;
        }
      }

      login(data.token, data.landlord);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
      setStep('code');
    } finally { setLoading(false); }
  }

  async function handleVerifyOTP(e) {
    e.preventDefault();
    await verifyOTP(email, code);
  }

  if (step === 'email') {
    return (
      <AuthShell title="Sign in" sub="Enter your email — we'll send you a login link">
        <form onSubmit={handleRequestOTP}>
          <FormField label="Email address">
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com" required autoFocus />
          </FormField>
          {error && <p style={{ color:'var(--danger)', fontSize:13, marginBottom:16 }}>{error}</p>}
          <button type="submit" disabled={loading} style={{ ...btnStyle.primary, opacity: loading ? .7 : 1 }}>
            {loading ? 'Sending…' : 'Send login link →'}
          </button>
        </form>
      </AuthShell>
    );
  }

  if (loading && searchParams.get('code')) {
    return (
      <AuthShell title="Signing you in…" sub="Please wait">
        <div style={{ textAlign:'center', padding:24, color:'var(--ink-3)' }}>Verifying your link…</div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Check your email" sub={`We sent a login link and 6-digit code to ${email}`}>
      <form onSubmit={handleVerifyOTP}>
        <FormField label="6-digit code">
          <input
            type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
            value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
            placeholder="000000"
            style={{ fontSize:28, letterSpacing:8, textAlign:'center', fontFamily:'var(--font-mono)' }}
            autoFocus required
          />
        </FormField>
        {error && <p style={{ color:'var(--danger)', fontSize:13, marginBottom:16 }}>{error}</p>}
        <button type="submit" disabled={loading} style={{ ...btnStyle.primary, opacity: loading ? .7 : 1 }}>
          {loading ? 'Verifying…' : 'Verify & sign in →'}
        </button>
        <button type="button" onClick={() => { setStep('email'); setCode(''); setError(''); }}
          style={{ ...btnStyle.ghost, marginTop:12, textAlign:'center' }}>
          ← Use a different email
        </button>
        <p style={{ textAlign:'center', marginTop:8, fontSize:12, color:'var(--ink-3)' }}>
          Didn't get it?{' '}
          <button type="button" onClick={handleRequestOTP}
            style={{ background:'none', border:'none', color:'var(--accent)', cursor:'pointer', fontSize:12, fontWeight:600 }}>
            Resend
          </button>
        </p>
      </form>
    </AuthShell>
  );
}

export { LoginPage as RegisterPage };